<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Right to Buy Mortgage Calculator</h2>
				<h5>Fill out the few details below to see how we could help you achieve a mortgage</h5>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
					
					<h4 class="calc-fieldset-title">Applicant Details</h4>

						<BooleanChoiceButtons
							fieldText="Number of applicants"
							buttonTextLeft="One applicant"
							buttonTextRight="Two applicants"							
						>
							<template v-slot:left>
								<input type="radio" v-model="applicantsCount" :value="1">										
							</template>
							<template v-slot:right>
								<input type="radio" v-model="applicantsCount" :value="2">										
							</template>																	
						</BooleanChoiceButtons>

						<transition name="fade-x" mode="out-in">
							<InputElement
								v-show="applicantsCount !==0"	
								fieldText="Applicant 1 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome1" />
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<InputElement
								v-show="applicantsCount === 2"						
								fieldText="Applicant 2 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome2" />
							</InputElement>
						</transition>
						
						<transition name="scale-in" mode="out-in">
							<ReturnedValueBox
								class="mb-5"
								text="Your household income is: "
								:value="householdIncome"
								v-show="annualIncome1 > 0 || annualIncome2 > 0"
								:innerElement=true
							/>
						</transition>

						<transition name="fade-x" mode="out-in">
							<InputElement					
								v-show="annualIncome1 > 0 || annualIncome2 > 0"
								fieldText="Number of dependant children?"
							>
								<InputTypeNumber v-model="dependantChildren" />
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="showPropertyDetails">
								<h4 class="calc-fieldset-title">Property Details</h4>

								<InputElement					
									fieldText="Value of property:"
								>
									<input class="form-control calc-form-control" type="number" v-model="valueProperty" min="0">
								</InputElement>								

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="valueProperty"					
										fieldText="What is the discounted price?"
									>
										<input class="form-control calc-form-control" type="number" v-model="discountedPrice" min="0">
									</InputElement>
								</transition>				

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="valueProperty && discountedPrice"				
										fieldText="Would you like to put in more deposit from your own resources, if so, how much:"
									>
										<input class="form-control calc-form-control" type="number" v-model="extraDeposit" min="0">
									</InputElement>
								</transition>					
							</div>
						</transition>

						<transition name="scale-in" mode="out-in">
							<div
								v-if="showCalculateButton"
								class="text-center mt-5 mb-3"
							>
								<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
							</div>
						</transition>

					</div>							

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted">

							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title">{{this.chance}}</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>

				</form>
			</div>				
		</div>

    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		ReturnedValueBox,
		SubmissionForm				
	},	
    data() {
        return {
			calculatorName: 'right-to-buy-mortgage-calculator',
            applicantsCount: 0,
            annualIncome1: null,
            annualIncome2: null,
            dependantChildren: null,
			valueProperty: null,
            discountedPrice: null,
            extraDeposit: null,
			chance: '',
			calcCompleted: false
        }
    },
    methods: {
        calculate() {
            if( this.totalRequired <= this.householdIncome * 4 ) {
                this.chance =  `You've got a good chance, please submit your details below to obtain your most suitable Right to Buy mortgage advice and the most suitable rates.`;
            }
            if( (this.totalRequired > this.householdIncome * 4) && (this.totalRequired <= this.householdIncome * 5) ) {
                this.chance = `We may be able to get you a Right to Buy Mortgage, please submit your details below to obtain your most suitable Right to Buy mortgage advice and the most suitable rates.`;
            }
            if( (this.totalRequired > this.householdIncome * 5) && (this.totalRequired <= this.householdIncome * 7) ) {
                this.chance = `It's looking difficult, but please submit your details below to obtain your most suitable Right to Buy mortgage advice.`;
            }
            if( this.totalRequired > this.householdIncome * 7 ) {
                this.chance = `It looks unlikely, but please submit your details below to obtain your most suitable Right to Buy mortgage advice.`;
            }
			this.calcCompleted = true;
        },
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
		},
		resetCalculation() {
			this.calcCompleted = false;
		}				
    },	
    computed: {
		showPropertyDetails() {
			return this.householdIncome && (this.dependantChildren || this.dependantChildren === 0);
		},		
		showCalculateButton() {
			return this.discountedPrice && (this.extraDeposit || this.extraDeposit === 0)
		},
        householdIncome() {
            let annualIncome1 = this.annualIncome1 ? this.annualIncome1 : 0;
            let annualIncome2 = this.annualIncome2 ? this.annualIncome2 : 0;

            return annualIncome1 + annualIncome2;
        },
        totalRequired() {
            let discountedPrice = this.discountedPrice ? this.discountedPrice : 0;
            let extraDeposit = this.extraDeposit ? this.extraDeposit : 0;

            return discountedPrice - extraDeposit;
        },
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Number of applicants: ${this.applicantsCount}
					Applicant 1 income: ${parseInt(this.annualIncome1)}
					Applicant 2 income: ${parseInt(this.annualIncome2)}
					Household income: £${this.householdIncome}
                    Number of dependant children: ${parseInt(this.dependantChildren)}
					Property Value: £ ${parseInt(this.valueProperty)}
					What is the Right to Buy discounted price? £${parseInt(this.discountedPrice)}
					Deposit amount: £${parseInt(this.extraDeposit)}
                    Total required: £${this.totalRequire}
				`
			}
			
			return calculatorData;
		}			
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>